import { KfzDokTypMap } from '@app/shared/gsm/gsm-ui-lib-elements/src/app/dynamic-form-components/haftpflicht-kfz-schaden/haftpflicht-kfz-schaden.types';
import { HaftpflichtSchadenKfz } from '@gsm/entities';
import { DYNAMIC_FORM_CONTROL_TYPE_GROUP, DYNAMIC_FORM_CONTROL_TYPE_INPUT, DYNAMIC_FORM_CONTROL_TYPE_SELECT, MATCH_VISIBLE } from '@ng-dynamic-forms/core';
import { MATCH_COMPARE_WITH_HIDDEN } from '../../matchers/compare-with-visible.matcher';
import { TYPE_DOKUMENTEN_ANGABE } from '../dokumenten-angabe/dokumenten-angabe.model';
import { TYPE_HAFTPFLICHT_GESCHAEDIGTE_PERSON } from '../haftpflicht-geschaedigte-person/haftpflicht-geschaedigte-person.model';
import { AnyFormControlModel } from '../shared-models/any-form-control.model';
import { HaftpflichtAdditional, HaftpflichtSchadenVorhanden } from '../shared-models/haftpflichtschaden.model';

const GESCHAEDIGTE_PERSON_RELATION = {
  match: MATCH_COMPARE_WITH_HIDDEN,
  when:  [
    {
      id:          'geschaedigtePerson',
      compareWith: (val: {
        vorhanden: string;
      }) => !val?.vorhanden || val.vorhanden === HaftpflichtSchadenVorhanden.Nachgebracht
    }
  ]
};

export function HAFTPFLICHT_KFZ_SCHADEN_TEMPLATE(
  value: HaftpflichtSchadenKfz,
  modelValue: any[],
  {
    docTypBelege,
    geschPersList,
    kundeList,
    updateClaim,
    paperProcessOverride
  }: HaftpflichtAdditional = {},
  dokTypMap: KfzDokTypMap,
  isEditEvent = false
): AnyFormControlModel[] {
  const {
    id,
    vorhanden,
    geschaedigtePerson,
    belege = [],
    schadenhoeheUeber1000
  } = value || {};
  const {
    kennzeichen,
    kennzeichenUnbekanntBegruendung
  } =
  geschaedigtePerson?.geschaedigtesFahrzeugList?.[0] || {};

  if (!isEditEvent && geschPersList && modelValue.length) {
    // only persons who are not in the local modelValue
    geschPersList = geschPersList.filter(
      geschPers => !modelValue.some(val => val.geschaedigtePerson?.correlationId === geschPers.correlationId)
    );
  }

  return [
    {
      id:         'geschaedigtePerson',
      type:       TYPE_HAFTPFLICHT_GESCHAEDIGTE_PERSON,
      value:      {
        id,
        vorhanden,
        geschaedigtePerson
      },
      additional: {
        geschPersList,
        updateClaim,
        paperProcessOverride,
        detailsNachbringen: false
      }
    },
    {
      id:        'schadenhoehe_group',
      type:      DYNAMIC_FORM_CONTROL_TYPE_GROUP,
      label:     'Schadenhöhe',
      relations: [GESCHAEDIGTE_PERSON_RELATION],
      group:     [
        {
          id:            'schadenhoehe_info',
          type:          'INFO',
          content:       'Beträgt die Schadenhöhe über € 1.000,- ?',
          isInstruction: true
        },
        {
          id:            'schadenhoeheUeber1000',
          type:          DYNAMIC_FORM_CONTROL_TYPE_SELECT,
          label:         'Schadenhöhe',
          required:      true,
          options:       [
            {
              label: 'Ja',
              value: true
            },
            {
              label: 'Nein',
              value: false
            }
          ],
          errorMessages: {
            required: '{{ label }} ist ein Pflichtfeld.'
          },
          value:         schadenhoeheUeber1000
        }
      ]
    },
    {
      id:        'kennzeichen_group',
      type:      DYNAMIC_FORM_CONTROL_TYPE_GROUP,
      label:     'Kennzeichen',
      relations: [GESCHAEDIGTE_PERSON_RELATION],
      group:     [
        {
          id:            'kennzeichen_info',
          type:          'INFO',
          content:       'Ist das Kennzeichen des KFZ bekannt?',
          isInstruction: true
        },
        {
          id:            'kennzeichenBekannt',
          label:         'Kennzeichen bekannt',
          type:          DYNAMIC_FORM_CONTROL_TYPE_SELECT,
          required:      true,
          options:       [
            {
              label: 'Ja',
              value: true
            },
            {
              label: 'Nein',
              value: false
            }
          ],
          additional:    {
            hideLog: true
          },
          errorMessages: {
            required: '{{ label }} ist ein Pflichtfeld.'
          },
          value:         kennzeichen ? true : kennzeichenUnbekanntBegruendung ? false : null
        },
        {
          id:            'kennzeichen',
          label:         'Kennzeichen',
          placeholder:   'Bitte geben Sie das Kennzeichen an.',
          type:          DYNAMIC_FORM_CONTROL_TYPE_INPUT,
          hidden:        true,
          required:      true,
          validators:    {
            required:  null,
            maxLength: 10,
            alphanum:  null
          },
          errorMessages: {
            required:  '{{label}} ist ein Pflichtfeld.',
            alphanum:  'Bitte geben Sie ein gültiges Kennzeichen ein.',
            maxLength: 'Bitte geben Sie maximal 10 Zeichen ein.'
          },
          relations:     [
            {
              match: MATCH_VISIBLE,
              when:  [
                {
                  id:    'kennzeichenBekannt',
                  value: true
                }
              ]
            }
          ],
          value:         kennzeichen
        },
        {
          id:            'kennzeichenUnbekanntBegruendung',
          label:         'Begründung',
          placeholder:   'Bitte geben Sie eine Begründung an, warum das Kennzeichen unbekannt ist.',
          validators:    {
            minLength: 2,
            required:  null,
            maxLength: 300
          },
          errorMessages: {
            required:  '{{ label }} ist ein Pflichtfeld.',
            minLength: 'Bitte geben Sie mindestens 2 Zeichen ein.',
            maxLength: 'Bitte geben Sie maximal 300 Zeichen ein.'
          },
          type:          DYNAMIC_FORM_CONTROL_TYPE_INPUT,
          hidden:        true,
          required:      true,
          relations:     [
            {
              match: MATCH_VISIBLE,
              when:  [
                {
                  id:    'kennzeichenBekannt',
                  value: false
                }
              ]
            }
          ],
          value:         kennzeichenUnbekanntBegruendung
        }
      ]
    },
    {
      id:        'aufstellungBelege_group',
      type:      DYNAMIC_FORM_CONTROL_TYPE_GROUP,
      label:     'Belege zum KFZ Schaden',
      hidden:    true,
      relations: [GESCHAEDIGTE_PERSON_RELATION],
      group:     [
        {
          id:         'aufstellungBelege',
          type:       TYPE_DOKUMENTEN_ANGABE,
          additional: {
            requiredTypes:       docTypBelege,
            optionalTypes:       docTypBelege,
            kunden:              kundeList,
            immediateValidation: true
          },
          value:      belege
        }
      ]
    }
  ];
}
